import { useState, useRef, useMemo } from 'react'
import { motion } from 'framer-motion'
import { EntityCard, FloatingActionButton, GlassPanel, Icon, LimitUpgradePrompt } from '../components/common'
import { EntityModal } from '../components/EntityModal'
import { useShots, useShotOperations } from '../hooks/useFirestore'
import { useProject } from '../context/ProjectContext'
import { useLimits } from '../hooks/useLimits'
import { deleteFiles, extractPublicIdFromUrl } from '../services/storage'
import { updateBidirectionalLinks, removeBidirectionalLinksOnDelete } from '../services/bidirectionalLinks'
import { COLLECTIONS } from '../services/firestore'
import './ShotsView.css'

// LocalStorage key for tier filter persistence
const TIER_FILTER_KEY = 'tierFilters_shots'

/**
 * Helper to get image URL from either string or object format
 * Supports both legacy (string) and new ({ url, publicId }) formats
 */
const getImageUrl = (image) => {
  if (!image) return ''
  if (typeof image === 'string') return image
  return image.url || ''
}

/**
 * Helper to collect all image publicIds from an entity
 * Returns array of publicIds for deletion
 */
const collectImagePublicIds = (entity) => {
  const publicIds = []
  
  // Primary image
  if (entity.primaryImage) {
    if (typeof entity.primaryImage === 'object' && entity.primaryImage.publicId) {
      publicIds.push(entity.primaryImage.publicId)
    } else if (typeof entity.primaryImage === 'string') {
      // Try to extract from URL for legacy data
      const extracted = extractPublicIdFromUrl(entity.primaryImage)
      if (extracted) publicIds.push(extracted)
    }
  }
  
  // Extra images
  if (entity.extraImages && Array.isArray(entity.extraImages)) {
    for (const img of entity.extraImages) {
      if (typeof img === 'object' && img.publicId) {
        publicIds.push(img.publicId)
      } else if (typeof img === 'string') {
        const extracted = extractPublicIdFromUrl(img)
        if (extracted) publicIds.push(extracted)
      } else if (typeof img === 'object' && img.url) {
        const extracted = extractPublicIdFromUrl(img.url)
        if (extracted) publicIds.push(extracted)
      }
    }
  }
  
  return publicIds.filter(Boolean)
}

// Page transition variants
const pageVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    filter: 'blur(4px)'
  },
  animate: { 
    opacity: 1, 
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    filter: 'blur(4px)',
    transition: {
      duration: 0.2
    }
  }
}

// Container variants for staggered children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
}

/**
 * Format duration for display
 * @param {number} seconds - Duration in seconds
 * @returns {string} - Formatted duration (e.g., "4.2s")
 */
const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return '—'
  return `${seconds.toFixed(1)}s`
}

/**
 * Get layer display label
 * @param {string} layer - Layer code (A/B/C/D)
 * @returns {string} - Display label
 */
const getLayerLabel = (layer) => {
  const labels = {
    'A': 'Layer A',
    'B': 'Layer B',
    'C': 'Layer C',
    'D': 'Layer D',
  }
  return labels[layer] || layer || '—'
}

/**
 * ShotsView - Grid display of shot cards with CRUD operations
 */
export default function ShotsView() {
  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('create')
  const [selectedShot, setSelectedShot] = useState(null)
  const [originRect, setOriginRect] = useState(null)
  const [limitPromptOpen, setLimitPromptOpen] = useState(false)
  const fabRef = useRef(null)

  // Tier filter state (default: A and B shown, C hidden)
  const [tierFilters, setTierFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(TIER_FILTER_KEY)
      return saved ? JSON.parse(saved) : { A: true, B: true, C: false }
    } catch {
      return { A: true, B: true, C: false }
    }
  })

  // Project context for scoped data access and permissions
  const { projectId, can } = useProject()
  const canCreate = can('create')
  
  // Limits checking
  const { canCreate: canCreateEntity, getLimit } = useLimits(projectId)

  // Firestore data and operations (project-scoped)
  const { data: shots, loading, error } = useShots(projectId, {
    orderByField: 'createdAt',
    orderDirection: 'desc',
  })
  const shotOps = useShotOperations(projectId)

  // Filter shots by tier
  const filteredShots = useMemo(() => {
    if (!shots) return []
    return shots.filter(shot => {
      const tier = (shot.tier || 'B').toUpperCase()
      return tierFilters[tier] !== false
    })
  }, [shots, tierFilters])

  // Toggle a tier filter
  const toggleTier = (tier) => {
    const newFilters = { ...tierFilters, [tier]: !tierFilters[tier] }
    setTierFilters(newFilters)
    try {
      localStorage.setItem(TIER_FILTER_KEY, JSON.stringify(newFilters))
    } catch {
      // Ignore localStorage errors
    }
  }

  // Handle create click - check limits first
  const handleCreateClick = (event) => {
    if (!canCreateEntity('shots')) {
      setLimitPromptOpen(true)
      return
    }
    openCreateModal(event)
  }

  // Open modal in create mode
  const openCreateModal = (event) => {
    setModalMode('create')
    setSelectedShot(null)
    if (event?.currentTarget) {
      setOriginRect(event.currentTarget.getBoundingClientRect())
    } else if (fabRef.current) {
      setOriginRect(fabRef.current.getBoundingClientRect())
    }
    setModalOpen(true)
  }

  // Open modal in edit mode
  const openEditModal = (shotData, clickEvent) => {
    setModalMode('edit')
    setSelectedShot(shotData)
    if (clickEvent?.currentTarget) {
      setOriginRect(clickEvent.currentTarget.getBoundingClientRect())
    }
    setModalOpen(true)
  }

  // Handle save (create or update)
  const handleSave = async (data) => {
    try {
      if (modalMode === 'create') {
        const newId = await shotOps.add(data)
        // Update bidirectional links for the new entity
        await updateBidirectionalLinks(projectId, COLLECTIONS.SHOTS, newId, null, data)
      } else {
        await shotOps.update(selectedShot.id, data)
        // Update bidirectional links (compare old vs new links)
        await updateBidirectionalLinks(projectId, COLLECTIONS.SHOTS, selectedShot.id, selectedShot, data)
      }
      setModalOpen(false)
    } catch (err) {
      console.error('Failed to save shot:', err)
      throw err
    }
  }

  // Handle delete (also deletes associated Cloudinary images and cleans up bidirectional links)
  const handleDelete = async () => {
    try {
      // Collect all image publicIds from the shot
      const publicIds = collectImagePublicIds(selectedShot)
      
      // Delete images from Cloudinary (don't fail if this fails)
      if (publicIds.length > 0) {
        console.log('Deleting Cloudinary images:', publicIds)
        const deleteResult = await deleteFiles(publicIds)
        if (deleteResult.errors?.length > 0) {
          console.warn('Some images failed to delete:', deleteResult.errors)
        }
      }
      
      // Clean up bidirectional links before deleting
      await removeBidirectionalLinksOnDelete(projectId, COLLECTIONS.SHOTS, selectedShot.id, selectedShot)
      
      // Delete the shot document from Firestore
      await shotOps.remove(selectedShot.id)
      setModalOpen(false)
    } catch (err) {
      console.error('Failed to delete shot:', err)
      throw err
    }
  }

  // Build card details from shot data
  const getCardDetails = (shot) => {
    const details = []
    
    // Duration
    details.push({ 
      label: 'Duration', 
      value: formatDuration(shot.durationSeconds)
    })
    
    // Layer
    if (shot.layer) {
      details.push({ 
        label: 'Layer', 
        value: shot.layer
      })
    }
    
    // Frame range
    if (shot.startFrame || shot.endFrame) {
      const frameRange = `${shot.startFrame || '?'} → ${shot.endFrame || '?'}`
      details.push({ 
        label: 'Frames', 
        value: frameRange.slice(0, 15) + (frameRange.length > 15 ? '...' : '')
      })
    }
    
    // Aspect ratio
    if (shot.aspectRatio) {
      details.push({ 
        label: 'Ratio', 
        value: shot.aspectRatio
      })
    }
    
    return details.slice(0, 4)
  }

  // Get name for card (shotId or title)
  const getName = (shot) => {
    return shot.shotId || shot.title || 'Untitled Shot'
  }

  // Get subtitle from description or title
  const getSubtitle = (shot) => {
    if (shot.description) {
      return shot.description.slice(0, 50) + (shot.description.length > 50 ? '...' : '')
    }
    if (shot.title && shot.shotId) {
      return shot.title
    }
    return 'No description'
  }

  return (
    <motion.div 
      className="shots-view"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Page Header */}
      <header className="shots-view__header">
        <div className="shots-view__title-group">
          <span className="shots-view__icon"><Icon name="shots" size={28} /></span>
          <h1 className="shots-view__title">Shots</h1>
        </div>
        <p className="shots-view__subtitle">
          {loading ? 'Loading...' : `${filteredShots.length} shot${filteredShots.length !== 1 ? 's' : ''}`}
          {!loading && shots.length !== filteredShots.length && ` (${shots.length} total)`}
        </p>
      </header>

      {/* Tier Filter Bar */}
      {!loading && !error && shots.length > 0 && (
        <div className="shots-view__filters">
          <div className="shots-view__tier-filters">
            <span className="shots-view__filter-label">Tier:</span>
            {['A', 'B', 'C'].map(tier => (
              <button
                key={tier}
                className={`shots-view__tier-btn shots-view__tier-btn--${tier.toLowerCase()} ${tierFilters[tier] ? 'shots-view__tier-btn--active' : ''}`}
                onClick={() => toggleTier(tier)}
                title={`${tierFilters[tier] ? 'Hide' : 'Show'} Tier ${tier} shots`}
              >
                {tier}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <GlassPanel glow="amber" padding="lg" className="shots-view__error">
          <h3>Error loading shots</h3>
          <p>{error.message}</p>
        </GlassPanel>
      )}

      {/* Loading State */}
      {loading && !error && (
        <div className="shots-view__loading">
          <div className="shots-view__spinner" />
          <p>Loading shots...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && shots.length === 0 && (
        <GlassPanel glow="amber" padding="xl" className="shots-view__empty">
          <div className="shots-view__empty-icon"><Icon name="shots" size={48} /></div>
          <h2>No shots yet</h2>
          <p>{canCreate ? 'Create your first shot to start building your storyboard.' : 'No shots have been added to this project yet.'}</p>
        </GlassPanel>
      )}

      {/* No Results State (when filters hide all) */}
      {!loading && !error && shots.length > 0 && filteredShots.length === 0 && (
        <div className="shots-view__no-results">
          <div className="shots-view__no-results-icon"><Icon name="search" size={48} /></div>
          <h3>No matching shots</h3>
          <p>Try adjusting your tier filters to see more shots.</p>
        </div>
      )}

      {/* Shot Grid */}
      {!loading && !error && filteredShots.length > 0 && (
        <motion.div 
          className="shots-view__grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredShots.map((shot, index) => (
            <EntityCard
              key={shot.id}
              type="shot"
              name={getName(shot)}
              image={getImageUrl(shot.primaryImage)}
              subtitle={getSubtitle(shot)}
              details={getCardDetails(shot)}
              tier={shot.tier}
              index={index}
              onClick={(e) => openEditModal(shot, e)}
            />
          ))}
        </motion.div>
      )}

      {/* Floating Action Button - only shown if user can create */}
      {canCreate && (
        <div ref={fabRef}>
          <FloatingActionButton
            icon={<Icon name="plus" size={24} />}
            variant="secondary"
            size="lg"
            position="bottom-right"
            label="Add new shot"
            onClick={handleCreateClick}
          />
        </div>
      )}

      {/* Entity Modal */}
      <EntityModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        entityType="shot"
        mode={modalMode}
        initialData={selectedShot}
        onSave={handleSave}
        onDelete={modalMode === 'edit' ? handleDelete : undefined}
        originRect={originRect}
      />

      {/* Limit Upgrade Prompt */}
      <LimitUpgradePrompt
        isOpen={limitPromptOpen}
        onClose={() => setLimitPromptOpen(false)}
        entityType="shots"
        limit={getLimit('shots')}
      />
    </motion.div>
  )
}

